import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Autocomplete from '@mui/material/Autocomplete';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import useUniqueId from '../hooks/useUniqueId';
import { getLabelFromJspVariables } from '../common/JspUtil';

const labelSx = {
    position: 'relative',
    transform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'text.primary',
    '&.Mui-focused': {
        color: 'text.primary',
    },
    '&.Mui-disabled': {
        color: 'text.primary',
    },
};

/**
 * MultiSelect
 * Props:
 *  - selectedValues: string[]
 *  - options: string[] | {label,value}[]
 *  - updateValues: (string[]) => void
 *  - label, required, error, helperText, disabled, placeholder
 *  - ...rest passed to Autocomplete
 */
const MultiSelect = ({ selectedValues = [], options = [], updateValues, placeholder, label, required, error, fullWidth, helperText, id, dataAutomationId, ...rest }) => {
    const automationId = dataAutomationId || id;
    const normalized = options.map(o => {
        if (o && typeof o === 'object' && 'value' in o && 'label' in o) return o;
        return { value: o, label: o };
    });
    const valueObjs = normalized.filter(o => selectedValues.includes(o.value));

    const fieldId = useUniqueId('multiselect');
    const helperId = `${fieldId}-helper`;

    return (
        <FormControl fullWidth={fullWidth} error={error} disabled={rest.disabled}>
            {label && (
                <InputLabel shrink required={required} sx={labelSx}>
                    {label}
                </InputLabel>
            )}
            <Autocomplete
                multiple
                fullWidth
                disableCloseOnSelect
                id={id}
                ListboxProps={automationId ? { 'data-automation-id': `${automationId}_listbox` } : undefined}
                options={normalized}
                value={valueObjs}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                onChange={(_, newValue) => updateValues(newValue.map(v => v.value))}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.value}>
                        <Checkbox
                            size="small"
                            checked={selected}
                            sx={{ mr: 1, p: 0 }}
                            inputProps={{ 'aria-label': option.label }}
                        />
                        {option.label}
                    </li>
                )}
                renderInput={(params) => (
                    <OutlinedInput
                        fullWidth
                        {...params.InputProps}
                        inputProps={{
                            ...params.inputProps,
                            'aria-invalid': error || undefined,
                            'aria-describedby': (helperText && helperId) || undefined,
                            ...(required ? { 'aria-required': true } : {}),
                            ...(automationId ? { 'data-automation-id': automationId } : {}),
                        }}
                        ref={params.InputProps.ref}
                        placeholder={!valueObjs.length ? (placeholder || getLabelFromJspVariables('label_select_values')) : ''}
                        size="small"
                        notched={false}
                    />
                )}
                ChipProps={{ size: 'small' }}
                sx={{
                    '& .MuiAutocomplete-tag': {
                        fontSize: '0.75rem',
                        height: '22px',
                    },
                }}
                {...rest}
            />
            <FormHelperText id={helperId} sx={{ my: 0 }}>{helperText || ' '}</FormHelperText>
        </FormControl>
    );
};

export default MultiSelect;
